import { useState, type FormEvent, type ReactNode } from 'react';
import { Page, PageHeader } from '../components/Page';
import { PCard } from '../components/PCard';
import { StatusPill } from '../components/StatusPill';
import { Async } from '../components/Async';
import { useFetch } from '../lib/useFetch';
import { useAuth } from '../lib/auth';
import { fmtCents } from '../lib/format';
import {
  getPricing,
  getPricingClientOptions,
  createPricing,
  updatePricing,
  deletePricing,
  setClientFlatRate,
  updateClientPharmacy,
  ApiError,
  type PricingClientOption,
  type PharmacyCadence,
} from '../lib/api';

const GLOBAL = 'global';

export const PricingPage = ({ tabs }: { tabs?: ReactNode } = {}) => {
  const { can } = useAuth();
  const manage = can('pricing:manage');
  const [scope, setScope] = useState<string>(GLOBAL); // 'global' or a clientId
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clientsState = useFetch(getPricingClientOptions);
  const pricingState = useFetch(
    () => (scope === GLOBAL ? getPricing({ scope: 'global' }) : getPricing({ clientId: scope })),
    [scope],
  );

  const clients = clientsState.data?.clients ?? [];
  const selectedClient = scope === GLOBAL ? undefined : clients.find((c) => c.id === scope);
  const scopeLabel = scope === GLOBAL ? 'Global default' : selectedClient?.name ?? 'Customer';

  async function run(fn: () => Promise<unknown>) {
    setError(null);
    try {
      await fn();
      pricingState.reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Action failed');
    }
  }

  return (
    <Page>
      <PageHeader
        tabs={tabs}
        title="Pricing"
        sub="Per-drug pharmacy pricing. Pick a customer to set their rates; unset drugs fall back to the global default."
        actions={
          manage ? (
            <button className="btn btn--primary" onClick={() => setAdding((a) => !a)}>
              {adding ? 'Cancel' : 'Add drug'}
            </button>
          ) : undefined
        }
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <label style={{ fontSize: 13, color: 'var(--p-muted-fg)' }}>Pricing for:</label>
        <select className="select" value={scope} onChange={(e) => { setScope(e.target.value); setAdding(false); }}>
          <option value={GLOBAL}>Global default</option>
          {clients.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name} ({c.type})
            </option>
          ))}
        </select>
      </div>

      {selectedClient && (
        <FlatRateCard
          key={selectedClient.id}
          client={selectedClient}
          canManage={manage}
          onChange={() => { clientsState.reload(); pricingState.reload(); }}
        />
      )}

      {error && (
        <div style={{ padding: '8px 12px', borderRadius: 6, background: 'rgba(239,68,68,.08)', color: 'var(--status-error-ink,#b91c1c)', fontSize: 13 }}>
          {error}
        </div>
      )}

      {adding && manage && (
        <AddPriceForm
          scopeLabel={scopeLabel}
          onCancel={() => setAdding(false)}
          onCreate={async (body) => {
            await run(() => createPricing({ ...body, clientId: scope === GLOBAL ? null : scope }));
            setAdding(false);
          }}
        />
      )}

      <Async state={pricingState} isEmpty={(d) => d.pricing.length === 0} emptyLabel={
        scope === GLOBAL ? 'No global prices yet.' : `No custom prices for ${scopeLabel} — they bill at the global default.`
      }>
        {({ pricing }) => (
          <PCard>
            <table className="p-table">
              <thead>
                <tr>
                  <th>Drug</th>
                  <th>Form</th>
                  <th>Strength</th>
                  <th>Size</th>
                  <th>Scope</th>
                  <th>Cost</th>
                  <th>Price</th>
                  <th>Margin</th>
                  {manage && <th style={{ textAlign: 'right' }}>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {pricing.map((r) => {
                  const margin = r.priceCents > 0 ? ((r.priceCents - r.costCents) / r.priceCents) * 100 : 0;
                  return (
                    <tr key={r.id}>
                      <td style={{ fontWeight: 500 }}>{r.drug}</td>
                      <td className="muted">{r.form}</td>
                      <td className="muted">{r.strength ?? '—'}</td>
                      <td className="muted">{r.size ?? '—'}</td>
                      <td>
                        <StatusPill tone={r.scope === 'client' ? 'in-progress' : 'neutral'}>
                          {r.scope === 'client' ? 'Customer' : 'Global'}
                        </StatusPill>
                      </td>
                      <td className="tabular muted">{fmtCents(r.costCents)}</td>
                      <td className="tabular">{fmtCents(r.priceCents)}</td>
                      <td className="tabular" style={{ color: 'var(--status-success-ink)' }}>{margin.toFixed(0)}%</td>
                      {manage && (
                        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                          <div style={{ display: 'inline-flex', gap: 8 }}>
                            <button className="btn" onClick={() => {
                              const v = window.prompt(`New price for ${r.drug} ${r.strength ?? ''} ${r.size ?? ''} (dollars):`, (r.priceCents / 100).toString());
                              const dollars = v && parseFloat(v);
                              if (dollars && dollars >= 0) run(() => updatePricing(r.id, { priceCents: Math.round(dollars * 100) }));
                            }}>Edit price</button>
                            <button className="btn" onClick={() => {
                              if (window.confirm(`Delete the ${r.scope} price for ${r.drug} ${r.strength ?? ''} ${r.size ?? ''}?`))
                                run(() => deletePricing(r.id));
                            }}>Delete</button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </PCard>
        )}
      </Async>
    </Page>
  );
};

function AddPriceForm({
  scopeLabel,
  onCreate,
  onCancel,
}: {
  scopeLabel: string;
  onCreate: (body: { drug: string; form: string; strength?: string; size?: string; costCents: number; priceCents: number }) => Promise<void>;
  onCancel: () => void;
}) {
  const [drug, setDrug] = useState('');
  const [form, setForm] = useState('Injectable');
  const [strength, setStrength] = useState('');
  const [size, setSize] = useState('');
  const [cost, setCost] = useState('');
  const [price, setPrice] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await onCreate({
        drug,
        form,
        strength: strength || undefined,
        size: size || undefined,
        costCents: Math.round((parseFloat(cost) || 0) * 100),
        priceCents: Math.round((parseFloat(price) || 0) * 100),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <PCard style={{ padding: 16 }}>
      <div style={{ fontSize: 13, color: 'var(--p-muted-fg)', marginBottom: 10 }}>
        Adding a price for <strong>{scopeLabel}</strong>
      </div>
      <form onSubmit={submit} style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
        <Field label="Drug"><input className="select" value={drug} onChange={(e) => setDrug(e.target.value)} required /></Field>
        <Field label="Form"><input className="select" value={form} onChange={(e) => setForm(e.target.value)} required /></Field>
        <Field label="Strength"><input className="select" value={strength} onChange={(e) => setStrength(e.target.value)} placeholder="e.g. 10mg/mL" /></Field>
        <Field label="Size"><input className="select" value={size} onChange={(e) => setSize(e.target.value)} placeholder="e.g. 4mL" /></Field>
        <Field label="Cost ($)"><input className="select" type="number" step="0.01" min="0" value={cost} onChange={(e) => setCost(e.target.value)} required /></Field>
        <Field label="Price ($)"><input className="select" type="number" step="0.01" min="0" value={price} onChange={(e) => setPrice(e.target.value)} required /></Field>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="submit" className="btn btn--primary" disabled={busy}>{busy ? 'Adding…' : 'Add price'}</button>
          <button type="button" className="btn" onClick={onCancel}>Cancel</button>
        </div>
      </form>
    </PCard>
  );
}

function FlatRateCard({ client, canManage, onChange }: { client: PricingClientOption; canManage: boolean; onChange: () => void }) {
  const flat = client.pharmacyFlatCents;
  const [val, setVal] = useState(flat != null ? (flat / 100).toString() : '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save(cents: number | null) {
    setBusy(true);
    setErr(null);
    try {
      await setClientFlatRate(client.id, cents);
      onChange();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not save');
    } finally {
      setBusy(false);
    }
  }

  return (
    <PCard style={{ padding: 16, borderLeft: flat != null ? '3px solid var(--status-attention-ink,#b45309)' : undefined }}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Flat per-script rate</div>
      <div className="muted" style={{ fontSize: 12, marginBottom: 10 }}>
        {flat != null ? (
          <>Every script for <strong>{client.name}</strong> bills at <strong>{fmtCents(flat)}</strong> regardless of product — the per-drug prices below are ignored (invoices stay itemized per product).</>
        ) : (
          <>Bill every script at one flat rate regardless of product (overrides the per-drug prices below; invoices stay itemized).</>
        )}
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <span style={{ color: 'var(--p-muted-fg)' }}>$</span>
        <input
          className="select"
          style={{ width: 100 }}
          type="number"
          step="0.01"
          min="0"
          value={val}
          onChange={(e) => setVal(e.target.value)}
          placeholder="per script"
          disabled={!canManage}
        />
        <button
          className="btn btn--primary"
          disabled={!canManage || busy || !val}
          onClick={() => { const d = parseFloat(val); if (!Number.isNaN(d) && d >= 0) save(Math.round(d * 100)); }}
        >
          {busy ? 'Saving…' : flat != null ? 'Update' : 'Set flat rate'}
        </button>
        {flat != null && canManage && (
          <button className="btn" disabled={busy} onClick={() => { setVal(''); save(null); }}>Clear</button>
        )}
      </div>
      {err && <div style={{ marginTop: 8, color: 'var(--status-error-ink,#b91c1c)', fontSize: 13 }}>{err}</div>}

      <PaymentTerms client={client} canManage={canManage} onChange={onChange} />
      <BillingOptions client={client} canManage={canManage} onChange={onChange} />
    </PCard>
  );
}

/** Per-client switches that change what a run produces: the dr network fee and
 *  whether pharmacy invoices are split by calendar month. */
function BillingOptions({ client, canManage, onChange }: { client: PricingClientOption; canManage: boolean; onChange: () => void }) {
  const [rate, setRate] = useState(client.drNetworkCents == null ? '' : String(client.drNetworkCents / 100));
  const [split, setSplit] = useState(client.monthlySplitInvoices);
  const [cadence, setCadence] = useState<PharmacyCadence>(client.pharmacyCadence ?? 'weekly');
  const [busy, setBusy] = useState(false);
  const monthly = cadence === 'monthly';

  async function save(next: { drNetworkCents?: number | null; monthlySplitInvoices?: boolean; pharmacyCadence?: PharmacyCadence }) {
    setBusy(true);
    try {
      await updateClientPharmacy(client.id, next);
      onChange();
    } finally {
      setBusy(false);
    }
  }

  function saveRate() {
    const trimmed = rate.trim();
    // Blank clears the fee; anything unparseable is left for the user to fix
    // rather than silently turning the fee off.
    if (trimmed === '') {
      if (client.drNetworkCents != null) save({ drNetworkCents: null });
      return;
    }
    const dollars = Number(trimmed);
    if (!Number.isFinite(dollars) || dollars < 0) return;
    const cents = Math.round(dollars * 100);
    if (cents !== client.drNetworkCents) save({ drNetworkCents: cents });
  }

  return (
    <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--p-border)' }}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Billing options</div>
      <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          Dr network $
          <input
            className="select"
            style={{ width: 72 }}
            type="number"
            min="0"
            step="0.01"
            placeholder="off"
            value={rate}
            disabled={!canManage || busy}
            onChange={(e) => setRate(e.target.value)}
            onBlur={saveRate}
          />
          per patient seen
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          Pharmacy invoices
          <select
            className="select"
            aria-label="Pharmacy invoice cadence"
            value={cadence}
            disabled={!canManage || busy}
            onChange={(e) => {
              const next = e.target.value as PharmacyCadence;
              setCadence(next);
              save({ pharmacyCadence: next });
            }}
          >
            <option value="weekly">Weekly (Sunday)</option>
            <option value="monthly">Monthly (calendar month)</option>
          </select>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--p-muted-fg)' }}>
          <input
            type="checkbox"
            checked={split || monthly}
            disabled={!canManage || busy || monthly}
            onChange={(e) => { setSplit(e.target.checked); save({ monthlySplitInvoices: e.target.checked }); }}
          />
          Split pharmacy invoices by calendar month
        </label>
      </div>
      <div className="muted" style={{ fontSize: 11, marginTop: 6 }}>
        {client.drNetworkCents == null
          ? 'No dr network fee. '
          : `Billed on the 1st for the month just closed, per patient prescribed or disqualified — wire/ACH, never auto-charged. `}
        {monthly
          ? 'Pharmacy & shipping is invoiced once a month, on the 1st, for the calendar month just closed; the Sunday run skips this client.'
          : split
            ? 'A run covering two months issues one invoice per month.'
            : 'A run issues one combined invoice whatever months it covers.'}
      </div>
    </div>
  );
}

function PaymentTerms({ client, canManage, onChange }: { client: PricingClientOption; canManage: boolean; onChange: () => void }) {
  const [days, setDays] = useState(String(client.pharmacyTermsDays ?? 3));
  const [onDue, setOnDue] = useState(client.pharmacyChargeOnDueDate);
  const [busy, setBusy] = useState(false);

  async function save(next: { pharmacyTermsDays?: number; pharmacyChargeOnDueDate?: boolean }) {
    setBusy(true);
    try {
      await updateClientPharmacy(client.id, next);
      onChange();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--p-border)' }}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Payment terms</div>
      <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          Net
          <input
            className="select"
            style={{ width: 64 }}
            type="number"
            min="0"
            value={days}
            disabled={!canManage}
            onChange={(e) => setDays(e.target.value)}
            onBlur={() => { const n = parseInt(days, 10); if (!Number.isNaN(n) && n !== client.pharmacyTermsDays) save({ pharmacyTermsDays: n }); }}
          />
          days
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--p-muted-fg)' }}>
          <input
            type="checkbox"
            checked={onDue}
            disabled={!canManage || busy}
            onChange={(e) => { setOnDue(e.target.checked); save({ pharmacyChargeOnDueDate: e.target.checked }); }}
          />
          Auto-charge on the due date (extended terms)
        </label>
      </div>
      <div className="muted" style={{ fontSize: 11, marginTop: 6 }}>
        {onDue
          ? `Invoiced Sunday, due in ${days} days, auto-charged on the due date (if a card is on file).`
          : `Invoiced Sunday, due in ${days} days, auto-charged the Monday after (if a card is on file).`}
      </div>
    </div>
  );
}

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: 'var(--p-muted-fg)' }}>
    {label}
    {children}
  </label>
);
